import { useState } from 'react';

const ContactForm = () => {
  // État pour les champs du formulaire
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    mail: '',
    telephone: '',
    message: '',
  });

  // État pour les erreurs de validation
  const [errors, setErrors] = useState({
    nom: '',
    prenom: '',
    mail: '',
    telephone: '',
    message: '',
  });

  // État pour le message de succès/erreur
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: '',
  });

  // Fonction pour nettoyer les entrées (éviter les attaques XSS)
  const sanitizeInput = (input, isTextarea = false) => {
    let sanitized = input.replace(/<[^>]*>/g, '') // Supprime les balises HTML
    if (!isTextarea) {
      sanitized = sanitized.trim(); // Supprime les espaces en début et fin de chaîne
    }
    return sanitized;
  };

  // Fonction pour valider les champs
  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...errors };

    // Validation du nom
    if (!formData.nom) {
      newErrors.nom = 'Le nom est obligatoire.';
      isValid = false;
    } else {
      newErrors.nom = '';
    }

    // Validation du prénom
    if (!formData.prenom) {
      newErrors.prenom = 'Le prénom est obligatoire.';
      isValid = false;
    } else {
      newErrors.prenom = '';
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.mail) {
      newErrors.mail = 'L\'email est obligatoire.';
      isValid = false;
    } else if (!emailRegex.test(formData.mail)) {
      newErrors.mail = 'L\'email n\'est pas valide.';
      isValid = false;
    } else {
      newErrors.mail = '';
    }

    // Validation du téléphone (optionnel)
    if (formData.telephone && !/^[\d\s+()-]{10,}$/.test(formData.telephone)) {
      newErrors.telephone = 'Le numéro de téléphone n\'est pas valide.';
      isValid = false;
    } else {
      newErrors.telephone = '';
    }

    // Validation du message
    if (!formData.message) {
      newErrors.message = 'Le message est obligatoire.';
      isValid = false;
    } else {
      newErrors.message = '';
    }

    setErrors(newErrors);
    return isValid;
  };

  // Gestion des changements dans les champs
  const handleChange = (e) => {
    const { name, value } = e.target;
    const isTextarea = name === 'message';
    setFormData({
      ...formData,
      [name]: sanitizeInput(value, isTextarea), // Nettoyage des entrées
    });
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return; // Arrête la soumission si la validation échoue
    }

    try {
      // Envoi des données au backend (exemple avec fetch)
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Ajoute un token CSRF si nécessaire (ex: 'X-CSRF-Token': csrfToken)
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || 'Erreur lors de l\'envoi du formulaire.');
      }

      // Réinitialisation du formulaire et affichage d'un message de succès
      setFormData({
        nom: '',
        prenom: '',
        mail: '',
        telephone: '',
        message: '',
      });
      setSubmitStatus({
        success: true,
        message: 'Votre message a été envoyé avec succès !',
      });
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: error.message || 'Une erreur est survenue. Veuillez réessayer plus tard.',
      });
      console.error('Erreur:', error);
    }
  };

  return (
    <form className="contact-form glass-effect" onSubmit={handleSubmit}>
      {/* Nom */}
      <div className="form-group">
        <label htmlFor="nom">Nom</label>
        <input
          type="text"
          id="nom"
          name="nom"
          value={formData.nom}
          onChange={handleChange}
          required
          className={errors.nom ? 'error' : ''}
        />
        {errors.nom && <span className="error-message">{errors.nom}</span>}
      </div>

      {/* Prénom */}
      <div className="form-group">
        <label htmlFor="prenom">Prénom</label>
        <input
          type="text"
          id="prenom"
          name="prenom"
          value={formData.prenom}
          onChange={handleChange}
          required
          className={errors.prenom ? 'error' : ''}
        />
        {errors.prenom && <span className="error-message">{errors.prenom}</span>}
      </div>

      {/* Email */}
      <div className="form-group">
        <label htmlFor="mail">Mail</label>
        <input
          type="email"
          id="mail"
          name="mail"
          value={formData.mail}
          onChange={handleChange}
          required
          className={errors.mail ? 'error' : ''}
        />
        {errors.mail && <span className="error-message">{errors.mail}</span>}
      </div>

      {/* Téléphone */}
      <div className="form-group">
        <label htmlFor="telephone">Numéro de téléphone</label>
        <input
          type="tel"
          id="telephone"
          name="telephone"
          value={formData.telephone}
          onChange={handleChange}
          className={errors.telephone ? 'error' : ''}
        />
        {errors.telephone && <span className="error-message">{errors.telephone}</span>}
      </div>

      {/* Message */}
      <div className="form-group"
        
      >
        <label htmlFor="message">Message</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          className={errors.message ? 'error' : ''}
        />
        {errors.message && <span className="error-message">{errors.message}</span>}
      </div>

      {/* Bouton de soumission */}
      <button type="submit" className="submit-btn">
        Envoyer
      </button>

      {/* Message de statut */}
      {submitStatus.message && (
        <div className={`status-message ${submitStatus.success ? 'success' : 'error'}`}>
          {submitStatus.message}
        </div>
      )}
    </form>
  );
};

export default ContactForm;